/**
 * Utilidades da DRE de grupo para os INDICADORES (Receita Líquida, Margem Bruta, Lucro Bruto...).
 *
 * Indicador é diferente de conta em dois pontos que mandam aqui:
 *
 * 1. Ele não tem `level`. É assim que o componente o trata como linha plana, sempre visível e não
 *    expansível.
 * 2. O `compound` dele são os operandos da fórmula, não os filhos na tela. A Margem Bruta, por
 *    exemplo, tem `compound: [Lucro Bruto, Receita Líquida]`, e o acumulado do percentual lê
 *    exatamente `compound[0] / compound[1]`. Sobrescrever esse campo para pendurar as empresas
 *    quebraria o cálculo — por isso a quebra por empresa vive em `companyBreakdown`.
 */

import { groupCompanyLabel } from "./group-company-label";

/** `associated` vem como array na base, mas há série antiga com string. */
const toAssociatedList = (associated) => [].concat(associated || []);

/**
 * Escolhe o indicador do GRUPO entre os candidatos de mesmo `associated`.
 *
 * O indicador do grupo e o das empresas-membro compartilham o `associated` (a Receita Líquida das
 * duas é `kNPRazOz9pnnXyJhHTT76`). Sem desempate, quem decide é a ordem em que o Mongo devolveu os
 * documentos — e a DRE do grupo pode exibir o indicador de UMA empresa rotulado como consolidado,
 * sem erro nenhum na tela. É a mesma armadilha já corrigida na raiz da DRE.
 *
 * Para empresa avulsa nada muda: não há `groupId` e o primeiro segue valendo.
 */
export function findIndicatorByAssociated(indicators = [], associatedId, groupId) {
    const candidates = indicators.filter((indicator) =>
        toAssociatedList(indicator.associated).includes(associatedId)
    );
    if (!candidates.length) return undefined;
    if (!groupId) return candidates[0];
    return (
        candidates.find((indicator) => indicator.companyId === groupId) ??
        candidates[0]
    );
}

/**
 * Pendura, sob cada indicador do grupo, o indicador equivalente de cada empresa-membro.
 *
 * O vínculo é o `associated`: o indicador do grupo não referencia as empresas em lugar nenhum (o
 * `compound` dele aponta para outras séries do próprio grupo), então é o `associated` compartilhado
 * que casa "Receita Líquida do grupo" com "Receita Líquida da Alfa".
 *
 * Só indicadores entram: as CONTAS já formam árvore pelo `compound` e são expandidas por lá — o
 * que as distingue aqui é justamente ter `level`.
 *
 * Devolve uma lista nova; os filhos são cópias, para não alterar as séries de origem.
 */
export function attachCompanyIndicators({
    list = [],
    indicators = [],
    groupId,
    companies = []
} = {}) {
    if (!groupId || !companies.length) return list;

    // Rótulo com CNPJ: as filiais de um grupo compartilham o nome.
    const labelByCompanyId = new Map(
        companies.filter((c) => c?._id).map((c) => [c._id, groupCompanyLabel(c)])
    );
    const result = [];

    for (const row of list) {
        result.push(row);

        if (typeof row.level !== "undefined") continue;
        if (row.companyId !== groupId) continue;

        const associatedIds = toAssociatedList(row.associated);
        if (!associatedIds.length) continue;

        const children = indicators.filter(
            (indicator) =>
                indicator.companyId !== groupId &&
                labelByCompanyId.has(indicator.companyId) &&
                toAssociatedList(indicator.associated).some((associated) =>
                    associatedIds.includes(associated)
                )
        );
        if (!children.length) continue;

        row.companyBreakdown = children.map((child) => child._id);

        for (const child of children) {
            result.push({
                ...child,
                father: row._id,
                level: "1",
                name: `${child.name} - ${labelByCompanyId.get(child.companyId)}`
            });
        }
    }

    return result;
}
